import type {
	AutomaticSourcesMode,
	PreviousStepDocumentsMode,
} from '@Application/Organizations/Workflows/Actions/organizationWorkflowsFetched';

export type SaveWorkflowAutomaticSource = {
	readonly documentTypeId: number;
	readonly verifiedOnly: boolean;
};

/** Input side: document types are bare ids, where the response renders `[{ id }]`. */
export type SaveWorkflowStepInputs = {
	readonly preparationElements: boolean;
	readonly previousStepDocumentsMode: PreviousStepDocumentsMode;
	readonly previousStepDocumentTypeIds: readonly number[];
	readonly automaticSourcesMode: AutomaticSourcesMode;
	readonly automaticSourceDocumentTypeIds: readonly number[];
};

/** A null prompt means "the catalogue default". */
export type SaveWorkflowStepReevaluation = {
	readonly enabled: boolean;
	readonly systemPrompt: string | null;
	readonly contextPrompt: string | null;
};

export type SaveWorkflowStep = {
	readonly documentTypeId: number;
	/** Null falls back to the document type's label. */
	readonly label: string | null;
	readonly systemPrompt: string | null;
	readonly contextPrompt: string | null;
	readonly inputs: SaveWorkflowStepInputs;
	readonly reevaluation: SaveWorkflowStepReevaluation;
	readonly splitCalls: boolean;
	/** Null when `splitCalls` is false. */
	readonly sectionDependencies: Readonly<Record<string, readonly string[]>> | null;
	readonly temperature: number | null;
	readonly vocabularyTerms: readonly string[] | null;
};

/** Steps of one stage run in parallel; stages run one after the other. */
export type SaveWorkflowStage = {
	readonly steps: readonly SaveWorkflowStep[];
};

export type SaveWorkflowDraft = {
	readonly label: string;
	readonly description: string | null;
	readonly acceptsInterviews: boolean;
	readonly acceptsNotes: boolean;
	readonly acceptsDocuments: boolean;
	readonly requiresValidationBeforeNext: boolean;
	readonly automaticSources: readonly SaveWorkflowAutomaticSource[];
	readonly stages: readonly SaveWorkflowStage[];
};

/**
 * Body of `POST …/workflows` (creation, `workflowKey` null) and `PUT …/workflows/{key}` (a new
 * version of an existing group). `organizationId` and `workflowKey` are route identifiers, never
 * echoed in the body.
 */
class SaveOrganizationWorkflow {
	constructor(
		public readonly organizationId: number,
		public readonly workflowKey: string | null,
		public readonly draft: SaveWorkflowDraft,
	) {
		Object.freeze(this);
	}

	toJSON(): Record<string, unknown> {
		const { draft } = this;
		return {
			label: draft.label,
			description: draft.description,
			acceptsInterviews: draft.acceptsInterviews,
			acceptsNotes: draft.acceptsNotes,
			acceptsDocuments: draft.acceptsDocuments,
			requiresValidationBeforeNext: draft.requiresValidationBeforeNext,
			automaticSources: draft.automaticSources.map((source) => ({
				documentTypeId: source.documentTypeId,
				verifiedOnly: source.verifiedOnly,
			})),
			stages: draft.stages.map((stage) => ({
				steps: stage.steps.map((step) => ({
					documentTypeId: step.documentTypeId,
					label: step.label,
					systemPrompt: step.systemPrompt,
					contextPrompt: step.contextPrompt,
					inputs: {
						preparationElements: step.inputs.preparationElements,
						previousStepDocumentsMode: step.inputs.previousStepDocumentsMode,
						previousStepDocumentTypeIds: step.inputs.previousStepDocumentTypeIds,
						automaticSourcesMode: step.inputs.automaticSourcesMode,
						automaticSourceDocumentTypeIds: step.inputs.automaticSourceDocumentTypeIds,
					},
					reevaluation: {
						enabled: step.reevaluation.enabled,
						systemPrompt: step.reevaluation.systemPrompt,
						contextPrompt: step.reevaluation.contextPrompt,
					},
					splitCalls: step.splitCalls,
					sectionDependencies: step.sectionDependencies,
					temperature: step.temperature,
					vocabularyTerms: step.vocabularyTerms,
				})),
			})),
		};
	}
}

export default SaveOrganizationWorkflow;
